import { Link } from 'react-router-dom';
import type { BlogPostMetadata } from '../types/blog';
import { Calendar, Clock, Tag } from 'lucide-react';

interface BlogCardProps {
  post: BlogPostMetadata;
  isLatest?: boolean;
}

export default function BlogCard({ post, isLatest = false }: BlogCardProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <article className="group overflow-hidden rounded-lg border border-border bg-card transition-all hover:shadow-lg">
      {post.coverImage && (
        <Link to={`/post/${post.slug}`}>
          <div className="aspect-video overflow-hidden">
            <img
              src={post.coverImage}
              alt={post.title}
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
            />
          </div>
        </Link>
      )}
      
      <div className="p-6">
        {(post.featured || isLatest) && (
          <div className="mb-3 flex flex-wrap gap-2">
            {isLatest && (
              <span className="inline-flex items-center rounded-full bg-green-500/10 px-3 py-1 text-xs font-medium text-green-500">
                Senaste inlägget
              </span>
            )}
            {post.featured && (
              <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-[#ed8b00]">
                Utvald artikel
              </span>
            )}
          </div>
        )}

        <Link to={`/post/${post.slug}`}>
          <h2 className="mb-2 text-2xl font-bold text-card-foreground transition-colors group-hover:text-[#ce7d0a]">
            {post.title}
          </h2>
        </Link>

        <p className="mb-4" style={{ color: '#aaa399' }}>{post.description}</p>

        <div className="mb-4 flex flex-wrap gap-2">
          {post.tags.map((tag) => (
            <Link
              key={tag}
              to={`/tag/${encodeURIComponent(tag)}`}
              className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
            >
              <Tag className="h-3 w-3" />
              {tag}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <time dateTime={post.publishedDate}>{formatDate(post.publishedDate)}</time>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>5 min läsning</span>
          </div>
        </div>

        {post.author && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Av: <span className="font-medium text-[#ebe8e0]">{post.author}</span>
            </p>
          </div>
        )}
      </div>
    </article>
  );
}
